import {
  Box,
  Button,
  Paper,
  Typography,
  Tabs,
  Tab,
  ListItem,
  List,
} from "@mui/material";
import { useState } from "react";
import { useModalContext } from "../../../context/ModalContext";
import { useAppDispatch } from "../../../store/hooks";
import { isValidNsnOrCc1 } from "../../../utils/genericUtils";
import {
  updateCnamBusinessesMiddleware,
  updateCnamFileUploadMiddleware,
  updateCnamPersonsMiddleware,
} from "../../../middleware/cnamMiddleware";
import { CnamRequestSubmissionPersonInputDTO } from "../../../model/input/cnam/cnamRequestSubmissionPersonsInputDTO";
import { CnamRequestSubmissionBusinessesInputDTO } from "../../../model/input/cnam/cnamRequestSubmissionBusinessesInputDTO";
import { NumberNameResponseModal } from "./NumberNameResponseModal";
import { BusinessEntryForm } from "./BusinessEntryForm";
import { ResidentialEntryForm } from "./ResidentialEntryForm";
import { FileUploadForm } from "./FileUploadForm";
import { CnamRequestSubmissionBulkInputDTO } from "../../../model/input/cnam/cnamRequestSubmissionBulkInputDTO";
import { HelpFormattingModalContent } from "./HelpModalContent";

export const MAX_UPLOADED_TNS = 1000;
export const BUSINESS_MAX_UPLOADS_SIZE = 200;

export interface FileUploadItem {
  phoneNumber: string;
  cnam: string;
}

export interface FileUploadErrorItem {
  phoneNumber: string;
  cnam: string;
  errorMessage: string;
  lineIndex: number;
}

export interface ResidentialEntry {
  id: string;
  phoneNumber: string;
  currentCnam: string;
  desiredCnam: string;
  comment: string;
}

export interface BusinessEntry {
  id: string;
  desiredCnam: string;
  currentCnam: string;
  comment: string;
  phoneNumbers: string; // Multi-line string of phone numbers
}

export interface ResidentialEntryFormError {
  id: string;
  key: keyof ResidentialEntry;
  error: string;
}

export interface BusinessEntryFormError {
  id: string;
  key: keyof BusinessEntry;
  error: string;
}

const BUSINESS_ENTRIES_INIT_STATE: BusinessEntry[] = [
  {
    id: Date.now().toString(),
    desiredCnam: "",
    currentCnam: "",
    comment: "",
    phoneNumbers: "",
  },
];

const RESIDENTIAL_ENTRIES_INIT_STATE: ResidentialEntry[] = [
  {
    id: Date.now().toString(),
    phoneNumber: "",
    currentCnam: "",
    desiredCnam: "",
    comment: "",
  },
];

const NumberNameRequest = () => {
  const { displayModal } = useModalContext();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState(0);

  const [residentialEntries, setResidentialEntries] = useState<
    ResidentialEntry[]
  >(RESIDENTIAL_ENTRIES_INIT_STATE);

  const [residentialEntriesErrors, setResidentialEntriesErrors] = useState<
    ResidentialEntryFormError[]
  >([]);

  const [businessEntries, setBusinessEntries] = useState<BusinessEntry[]>(
    BUSINESS_ENTRIES_INIT_STATE
  );

  const [businessEntriesErrors, setBusinessEntriesErrors] = useState<
    BusinessEntryFormError[]
  >([]);

  const [fileUploadItems, setFileUploadItems] = useState<FileUploadItem[]>([]);
  const [fileUploadErrorItems, setFileUploadErrorItems] = useState<
    FileUploadErrorItem[]
  >([]);
  const [comment, setComment] = useState<string>("");

  const validateAndSubmitResidentials = async () => {
    const errors: ResidentialEntryFormError[] = [];

    if (residentialEntries.length > MAX_UPLOADED_TNS) {
      residentialEntries.forEach((entry) => {
        errors.push({
          id: entry.id,
          key: "phoneNumber",
          error: "Cannot upload more than 1,000 entries at a time",
        });
      });
      setResidentialEntriesErrors(errors);
      return;
    }

    // Validate person entries
    residentialEntries.forEach((entry, index) => {
      if (!entry.phoneNumber.trim()) {
        errors.push({
          id: entry.id,
          key: "phoneNumber",
          error: "Phone Number cannot be Empty",
        });
      } else if (!isValidNsnOrCc1(entry.phoneNumber)) {
        errors.push({
          id: entry.id,
          key: "phoneNumber",
          error: "Invalid phone number format",
        });
      }
      if (!entry.desiredCnam.trim()) {
        errors.push({
          id: entry.id,
          key: "desiredCnam",
          error: "Expected Number Name is required",
        });
      } else if (entry.desiredCnam.length > 15) {
        errors.push({
          id: entry.id,
          key: "desiredCnam",
          error: `Expected Number Name must be 15 characters or less`,
        });
      }
    });

    residentialEntries.forEach((e1, ind1) => {
      residentialEntries.forEach((e2, ind2) => {
        if (e1.phoneNumber === e2.phoneNumber && ind1 !== ind2) {
          errors.push({
            id: e1.id,
            key: "phoneNumber",
            error: `You have added two Duplicate TNs!`,
          });
        }
      });
    });

    setResidentialEntriesErrors(errors);

    if (errors.length > 0) {
      return;
    }

    console.log("Submitting CNAM requests:", {
      residentialEntries,
      businessEntries,
    });

    let input: CnamRequestSubmissionPersonInputDTO = { data: [] };

    residentialEntries.forEach((e) => {
      input.data.push({
        tn: e.phoneNumber,
        desiredCName: e.desiredCnam,
        currentCName: e.currentCnam,
        comment: e.comment,
      });
    });

    dispatch(updateCnamPersonsMiddleware(input));
  };

  const validateAndSubmitBusinesses = () => {
    const errors: BusinessEntryFormError[] = [];

    let x = businessEntries.reduce((prev, curr) => {
      return prev + curr.phoneNumbers.split("\n").length;
    }, 0);

    // If they're trying to submit more than 1,000 TNs total, return an error
    if (
      businessEntries.reduce((prev, curr) => {
        return prev + curr.phoneNumbers.split("\n").length;
      }, 0) > MAX_UPLOADED_TNS
    ) {
      businessEntries.forEach((entry) => {
        errors.push({
          id: entry.id,
          key: "phoneNumbers",
          error: `Cannot upload more than ${MAX_UPLOADED_TNS} updates at a time`,
        });
      });
      setBusinessEntriesErrors(errors);
      return;
    }

    // Validate business entries
    businessEntries.forEach((entry, index) => {
      if (!entry.desiredCnam.trim()) {
        errors.push({
          id: entry.id,
          key: "desiredCnam",
          error: `Expected Number Name is required`,
        });
      } else if (entry.desiredCnam.length > 15) {
        errors.push({
          id: entry.id,
          key: "desiredCnam",
          error: `Expected Number Name must be 15 characters or less`,
        });
      }

      if (!entry.phoneNumbers.trim()) {
        errors.push({
          id: entry.id,
          key: "phoneNumbers",
          error: `At least one phone number is required`,
        });
      } else {
        const phoneList = entry.phoneNumbers
          .split("\n")
          .filter((phone) => phone.trim() !== "");
        const invalidPhones = phoneList.filter(
          (phone) => !isValidNsnOrCc1(phone.trim())
        );

        if (phoneList.length > BUSINESS_MAX_UPLOADS_SIZE) {
          errors.push({
            id: entry.id,
            key: "phoneNumbers",
            error: `Maximum of ${BUSINESS_MAX_UPLOADS_SIZE} updates per business at a time (Currently ${phoneList.length})`,
          });
        }

        if (invalidPhones.length > 0) {
          errors.push({
            id: entry.id,
            key: "phoneNumbers",
            error: `Invalid phone numbers: ${invalidPhones.join(", ")}`,
          });
        }
      }
    });

    // Make sure they didn't enter the same TN in different Business inputs
    businessEntries.forEach((entry) => {
      businessEntries.forEach((otherEntry) => {
        let tnList1 = entry.phoneNumbers.split("\n");
        let tnList2 = otherEntry.phoneNumbers.split("\n");

        tnList1.forEach((tn1, ind1) => {
          tnList2.forEach((tn2, ind2) => {
            // Checks for matches across different inputs
            if (entry.id !== otherEntry.id && tn1 === tn2) {
              errors.push({
                id: entry.id,
                key: "phoneNumbers",
                error: `You submitted the same TN in two different inputs! TN: ${tn1}`,
              });
            }

            // Checks for matches across the same input
            if (entry.id === otherEntry.id && tn1 === tn2 && ind1 !== ind2) {
              errors.push({
                id: entry.id,
                key: "phoneNumbers",
                error: `This input contains the same TN twice! TN: ${tn1}`,
              });
            }
          });
        });
      });
    });

    setBusinessEntriesErrors(errors);

    if (errors.length > 0) {
      return;
    }

    let input: CnamRequestSubmissionBusinessesInputDTO = { data: [] };

    businessEntries.forEach((e) => {
      input.data.push({
        tns: e.phoneNumbers.split("\n").map((tn) => tn.trim()),
        desiredCName: e.desiredCnam,
        currentCName: e.currentCnam,
        comment: e.comment,
      });
    });

    dispatch(updateCnamBusinessesMiddleware(input));
  };

  const submitFileUploadTns = () => {
    if (fileUploadItems.length === 0 || fileUploadErrorItems.length !== 0) {
      return;
    }
    let input: CnamRequestSubmissionBulkInputDTO = {
      data: fileUploadItems.map((e) => {
        return {
          tn: e.phoneNumber,
          desiredCName: e.cnam,
        };
      }),
      comment: comment,
    };

    dispatch(updateCnamFileUploadMiddleware(input));
  };

  const onClickHelpFormatting = () => {
    displayModal(
      <HelpFormattingModalContent includeFileTips={activeTab == 2} />,
      "Help Formatting",
      { width: "60%" },
      true
    );
  };

  return (
    <Box sx={{ padding: 3, width: "80%" }}>
      <NumberNameResponseModal
        clearData={() => {
          setResidentialEntries(RESIDENTIAL_ENTRIES_INIT_STATE);
          setResidentialEntriesErrors([]);
          setBusinessEntries(BUSINESS_ENTRIES_INIT_STATE);
          setBusinessEntriesErrors([]);
          setFileUploadItems([]);
          setFileUploadErrorItems([]);
        }}
      />
      <Typography variant="h4" gutterBottom>
        Number Name Portal
      </Typography>

      <Typography variant="h6" sx={{ mb: 3 }}>
        Submit information for Number Name telephone numbers.
      </Typography>

      <Paper sx={{ padding: 3 }} elevation={4}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label={`Submit Residential`} />
          <Tab label={`Submit Businesses`} />
          <Tab label={`Submit File`} />
        </Tabs>

        {activeTab === 0 && (
          <ResidentialEntryForm
            residentialEntries={residentialEntries}
            setResidentialEntries={setResidentialEntries}
            residentialEntriesErrors={residentialEntriesErrors}
            setResidentialEntriesErrors={setResidentialEntriesErrors}
          />
        )}

        {activeTab === 1 && (
          <BusinessEntryForm
            businessEntries={businessEntries}
            setBusinessEntries={setBusinessEntries}
            businessEntriesErrors={businessEntriesErrors}
            setBusinessEntriesErrors={setBusinessEntriesErrors}
          />
        )}

        {activeTab === 2 && (
          <FileUploadForm
            fileUploadItems={fileUploadItems}
            setFileUploadItems={setFileUploadItems}
            fileUploadErrorItems={fileUploadErrorItems}
            setFileUploadErrorItems={setFileUploadErrorItems}
            comment={comment}
            setComment={setComment}
          />
        )}

        {activeTab < 3 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button variant="outlined" onClick={onClickHelpFormatting}>
              Help {activeTab === 2 ? "CSV File" : ""} Formatting
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={
                activeTab == 0
                  ? validateAndSubmitResidentials
                  : activeTab == 1
                  ? validateAndSubmitBusinesses
                  : submitFileUploadTns
              }
            >
              Submit Number Name{" "}
              {activeTab === 0
                ? "Residential "
                : activeTab === 0
                ? "Business "
                : "File "}
              Update
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NumberNameRequest;
